"use client";
import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const CLASH = 'var(--font-clash), "Clash Display", sans-serif';
const MANROPE = 'var(--font-manrope), Manrope, sans-serif';
const MONO = '"SF Mono", "Roboto Mono", Menlo, Consolas, monospace';
const INK = '#080400';
const LIME = '#ceff1a';
const PURPLE_LIGHT = '#a5b4fc';

interface Touchpoint {
    label: string;
    pct: number;
}

const touchpoints: Touchpoint[] = [
    { label: 'Google Ads', pct: 35 },
    { label: 'Instagram', pct: 22 },
    { label: 'Email', pct: 18 },
    { label: 'Direct', pct: 25 },
];

function TerminalConsole() {
    return (
        <div className="relative w-full max-w-[420px] mx-auto lg:mx-0">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
                className="relative rounded-2xl overflow-hidden"
                style={{ background: '#111111', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 30px 70px -24px rgba(0,0,0,0.6)' }}
            >
                <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.02)' }}>
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ff5f56' }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#ffbd2e' }} />
                    <span className="w-2.5 h-2.5 rounded-full" style={{ background: '#27c93f' }} />
                    <span className="ml-2 text-[11px]" style={{ fontFamily: MONO, color: 'rgba(255,255,255,0.4)' }}>
                        attribution.query
                    </span>
                </div>

                <div className="p-5 flex flex-col gap-3" style={{ fontFamily: MONO }}>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: 0.5 }}
                        className="text-[13px]"
                    >
                        <span style={{ color: LIME }}>$</span>{' '}
                        <span style={{ color: 'rgba(255,255,255,0.85)' }}>analyze --last-touch=false</span>
                    </motion.div>
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.3, delay: 0.65 }}
                        className="text-[11px]"
                        style={{ color: 'rgba(255,255,255,0.35)' }}
                    >
                        scanning touchpoints across journey...
                    </motion.div>

                    <div className="flex flex-col gap-2.5 mt-1">
                        {touchpoints.map((t, i) => (
                            <motion.div
                                key={t.label}
                                initial={{ opacity: 0, x: -8 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.35, delay: 0.8 + i * 0.12 }}
                                className="flex items-center gap-3"
                            >
                                <span className="text-[11px] w-[70px] shrink-0" style={{ color: 'rgba(255,255,255,0.6)' }}>
                                    {t.label}
                                </span>
                                <div className="flex-1 h-3 rounded-sm overflow-hidden" style={{ background: 'rgba(255,255,255,0.08)' }}>
                                    <motion.div
                                        initial={{ width: 0 }}
                                        whileInView={{ width: `${t.pct}%` }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.6, delay: 0.9 + i * 0.12, ease: 'easeOut' }}
                                        className="h-full"
                                        style={{ background: PURPLE_LIGHT }}
                                    />
                                </div>
                                <span className="text-[11px] w-9 text-right shrink-0" style={{ color: PURPLE_LIGHT }}>
                                    {t.pct}%
                                </span>
                            </motion.div>
                        ))}
                    </div>

                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: 1.5 }}
                        className="flex items-center gap-2 mt-2 pt-3 text-[13px]"
                        style={{ borderTop: '1px dashed rgba(255,255,255,0.12)' }}
                    >
                        <span style={{ color: LIME }}>&gt;</span>
                        <span style={{ color: '#ffffff' }}>conversion: booked</span>
                        <span style={{ color: LIME }}>✓</span>
                        <motion.span
                            className="w-[7px] h-[15px] ml-0.5"
                            style={{ background: LIME }}
                            animate={{ opacity: [1, 1, 0, 0] }}
                            transition={{ duration: 1, repeat: Infinity, times: [0, 0.5, 0.5, 1] }}
                        />
                    </motion.div>
                </div>
            </motion.div>

            <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 1.7 }}
                className="text-center mt-5 font-semibold text-sm tracking-[0.02em]"
                style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.4)' }}
            >
                every touchpoint counted, not just the last click
            </motion.p>
        </div>
    );
}

export default function AnalyticsHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative pt-36 pb-24 px-6 md:px-16 lg:px-24 overflow-hidden" style={{ background: INK }}>
            <div
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{ backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)', backgroundSize: '34px 34px' }}
            />
            <div
                className="absolute pointer-events-none"
                style={{
                    top: '-18%', right: '-10%', width: 620, height: 560,
                    background: 'radial-gradient(circle, rgba(165,180,252,0.12) 0%, transparent 68%)',
                    borderRadius: '42% 58% 63% 37% / 41% 44% 56% 59%',
                }}
            />

            <div className="max-w-[1360px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">

                <div className="lg:col-span-7 flex flex-col items-start gap-7 text-left relative">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="flex items-center gap-2 text-xs md:text-sm font-medium"
                        style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.4)' }}
                    >
                        <Link href="/services/performance-marketing" className="hover:underline" style={{ color: 'rgba(255,255,255,0.4)' }}>
                            Performance Marketing
                        </Link>
                        <span>/</span>
                        <span style={{ color: LIME }}>Marketing Analytics &amp; Attribution</span>
                    </motion.div>

                    <motion.h1
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-4xl md:text-5xl lg:text-[3rem] font-bold leading-[1.1] tracking-tight"
                        style={{ fontFamily: CLASH, color: '#ffffff' }}
                    >
                        Marketing Analytics &amp; Attribution That{' '}
                        <span style={{ color: LIME }}>Connects Activity to Outcomes</span>
                    </motion.h1>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.22 }}
                        className="flex flex-col gap-4 max-w-xl"
                    >
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.62)', letterSpacing: '0.01em' }}>
                            If you can&apos;t reliably measure where your leads and customers come from, you&apos;re making marketing decisions with incomplete information.
                        </p>
                        <p className="text-base md:text-lg leading-relaxed" style={{ fontFamily: MANROPE, color: 'rgba(255,255,255,0.62)', letterSpacing: '0.01em' }}>
                            Lead Workz builds measurement systems that connect campaigns, websites, customer journeys and conversions so you can understand what&apos;s actually contributing to growth.
                        </p>
                        <p className="text-base md:text-lg font-semibold" style={{ fontFamily: MANROPE, color: LIME, letterSpacing: '0.01em' }}>
                            Track less vanity. Understand more business.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.32 }}
                        className="pt-1"
                    >
                        <button
                            onClick={openModal}
                            className="inline-flex items-center justify-center px-8 py-3.5 rounded-full font-semibold text-base transition-all duration-300 hover:scale-105"
                            style={{ background: LIME, color: INK, fontFamily: CLASH, letterSpacing: '0.06em' }}
                        >
                            Get a Performance Marketing Analytics Audit →
                        </button>
                    </motion.div>
                </div>

                <div className="lg:col-span-5">
                    <TerminalConsole />
                </div>

            </div>
        </section>
    );
}
